import { memo, Suspense, useCallback, useMemo } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import clsx from 'clsx';
import { getApp } from '@/apps/registry';
import { showContextMenu, showMenuUnder } from '@/shell/ContextMenu/ContextMenu';
import { systemMenu } from '@/shell/ContextMenu/menus';
import { fsApi } from '@/fs/useFs';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Hourglass } from '@/components/Hourglass';
import { Icon } from '@/components/Icon';
import { DRAG_HANDLE } from './constants';
import { handleFor, useWindowStore } from './store';
import { useDrag } from './useDrag';
import { useResize } from './useResize';
import css from './Window.module.css';
import type { ResizeDir, WindowState } from './types';

const RESIZE_DIRS: readonly ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

function stopPointer(event: ReactPointerEvent<HTMLButtonElement>) {
  // Belt and braces: `useDrag` already ignores pointerdowns inside the
  // controls, but a caption button must never be the start of a drag.
  event.stopPropagation();
}

/**
 * Chrome around one running app: caption, caption buttons, resize handles.
 *
 * Memoised because a drag rewrites this window's state every animation frame —
 * without it, every other open window would re-render alongside.
 */
export const Window = memo(function Window({ win }: { win: WindowState }) {
  const manifest = getApp(win.appId);
  // A skinned app draws its own frame and caption. It is still a window in
  // every other respect — dragged, focused, minimized, listed in the taskbar
  // — so only the chrome changes here, never the machinery.
  const chromeless = manifest?.chromeless === true;

  const drag = useDrag(win, chromeless ? `.${DRAG_HANDLE}` : undefined);
  const resize = useResize(win);

  const focusSelf = useCallback(() => {
    // Capture phase: this has to land before an iframe in the client area gets
    // a chance to swallow the event.
    useWindowStore.getState().focus(win.id);
  }, [win.id]);

  const minimize = useCallback(() => useWindowStore.getState().minimize(win.id), [win.id]);
  const toggleMaximize = useCallback(
    () => useWindowStore.getState().toggleMaximize(win.id),
    [win.id],
  );
  const close = useCallback(() => useWindowStore.getState().close(win.id), [win.id]);

  /**
   * The System menu, from either of its two doors: the caption icon, which
   * opens it on an ordinary click, and a right-click anywhere along the
   * caption. Both were true of every window in Windows, and the icon is the
   * one people reach for when a window has wandered off the screen.
   */
  const onCaptionMenu = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      showContextMenu(event, systemMenu(win));
    },
    [win],
  );

  const onIconClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      showMenuUnder(event.currentTarget, systemMenu(win));
    },
    [win],
  );

  const onTitleDoubleClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const target = event.target as Element | null;
      if (target?.closest('.title-bar-controls')) return;
      // On a chromeless window this is bound to the whole frame, so it has to
      // follow the same rule dragging does: the caption only. Otherwise a
      // double-click anywhere inside the app would maximize it.
      if (chromeless && !target?.closest(`.${DRAG_HANDLE}`)) return;
      if (!win.maximizable) return;
      useWindowStore.getState().toggleMaximize(win.id);
    },
    [win.id, win.maximizable, chromeless],
  );

  // Held apart from the chrome so dragging — which changes `win` 60 times a
  // second — never re-renders the running app.
  const content = useMemo(() => {
    if (!manifest) return null;
    const App = manifest.component;
    return (
      <ErrorBoundary appName={manifest.name}>
        <Suspense fallback={<Hourglass />}>
          <App
            windowId={win.id}
            args={win.args}
            argsVersion={win.argsVersion}
            fs={fsApi}
            wm={handleFor(win.id)}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }, [manifest, win.id, win.args, win.argsVersion]);

  if (!manifest) {
    if (import.meta.env.DEV) {
      console.warn(`[wm] window ${win.id} references unknown app "${win.appId}"`);
    }
    return null;
  }

  const showHandles = win.resizable && !win.maximized;

  return (
    <div
      className={clsx(
        'window',
        css.win,
        chromeless && css.chromeless,
        !win.focused && css.inactive,
        win.maximized && css.maximized,
        win.minimized && css.hidden,
      )}
      style={{
        transform: `translate(${win.x}px, ${win.y}px)`,
        width: win.w,
        height: win.h,
        zIndex: win.zIndex,
      }}
      onPointerDownCapture={focusSelf}
      // Tabbing into a window has to activate it too, or the shell's keyboard
      // shortcuts act on whichever window the store still thinks is focused.
      onFocusCapture={focusSelf}
      role="dialog"
      aria-label={win.title}
      // Chromeless windows have no caption of their own to hang these on, so
      // the frame carries them and `useDrag` filters down to the app's own
      // caption strip. On a normal window they stay on the title bar.
      {...(chromeless ? drag : null)}
      onDoubleClick={chromeless ? onTitleDoubleClick : undefined}
    >
      {chromeless ? null : (
        <div
          className={clsx('title-bar', css.titleBar)}
          onDoubleClick={onTitleDoubleClick}
          onContextMenu={onCaptionMenu}
          {...drag}
        >
          <div className={clsx('title-bar-text', css.titleText)}>
            {/* Double-clicking the icon closed the window in XP, which is why
                this one has to swallow the event: the caption behind it reads
                the same gesture as "maximize". */}
            <button
              type="button"
              className={css.sysIcon}
              aria-label="System menu"
              aria-haspopup="menu"
              onPointerDown={stopPointer}
              onClick={onIconClick}
              onDoubleClick={(event) => {
                event.stopPropagation();
                close();
              }}
            >
              <Icon src={win.icon} size={16} />
            </button>
            <span className={css.titleLabel}>{win.title}</span>
          </div>
          <div className="title-bar-controls">
            {!win.dialog && (
              <button type="button" aria-label="Minimize" onPointerDown={stopPointer} onClick={minimize} />
            )}
            {win.maximizable && (
              <button
                type="button"
                aria-label={win.maximized ? 'Restore' : 'Maximize'}
                onPointerDown={stopPointer}
                onClick={toggleMaximize}
              />
            )}
            <button type="button" aria-label="Close" onPointerDown={stopPointer} onClick={close} />
          </div>
        </div>
      )}

      <div className={clsx('window-body', css.body, win.dialog && 'window-body--padded')}>
        {content}
      </div>

      {showHandles &&
        RESIZE_DIRS.map((dir) => <div key={dir} aria-hidden="true" {...resize.handleProps(dir)} />)}
    </div>
  );
});
